import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SearchInput } from '../_models/analytics/searchInput';
import { Filter } from '../_models/analytics/filter';
import { DateRange } from '../_models/analytics/dateRange';
import { GeoFilter } from '../_models/analytics/geoFilter';
import { XYearService } from './xYear.service';

@Injectable()
export class AnalyticsService {
  apiAnalyticsUrl = environment.apiAnalyticsUrl;
  tabFinance: BehaviorSubject<any> = new BehaviorSubject<any>('company');
  commoditiesSelected$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  companyIdToFilter = [];
  companyIdGroupSelected = [];
  yearSelected;

  constructor(
    private http: HttpClient,
    private xYearService: XYearService
  ) {
    this.xYearService.selectedYear$.subscribe((year) => {
      this.yearSelected = year;
    });
  }

  initTabFinance() {
    this.tabFinance.next('company');
  }

  generateSearchObject(
    queryFilter: Array<Filter>,
    aggregationType: string,
    aggregationField,
    subAggr = [],
    dateRange?: DateRange,
    fieldsPattern?: Array<string>,
    existFields?: Array<string>,
    interval?: string,
    geoQueryFilters?: Array<GeoFilter>,
    aggregationFilter?: Array<Filter>
  ) {
    const search = new SearchInput();
    search.queryFilters = queryFilter;
    if (geoQueryFilters) {
      search.geoQueryFilters = geoQueryFilters;
    }
    search.dateRange = dateRange;
    search.fieldsPattern = fieldsPattern;
    search.existFields = existFields;
    search.aggregation = {
      name: '',
      aggregationType,
      field: aggregationField,
      interval,
      subAggregations: subAggr,
      aggregationFilter,
    };
    return search;
  }

  getAnalytics(
    domain: string,
    type: string,
    obj: SearchInput,
    applyCompanyIdFilter: boolean = true,
    paginationFrom: number = null
  ): Observable<any> {
    let pagination = '';
    if (paginationFrom != null && paginationFrom != 0) pagination = '?from=' + paginationFrom;

    let cropYear = '';
    if (pagination == '') {
      cropYear = '?cropYear=' + this.yearSelected;
    } else {
      cropYear = '&cropYear=' + this.yearSelected;
    }

    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      if (domain === 'users') {
        obj.queryFilters.push({ field: 'farm.companyId', values: this.companyIdToFilter });
      } else {
        obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
      }
    }

    return this.http
      .post<any>(
        this.apiAnalyticsUrl +
          'analytics/supply-chain/' +
          domain +
          '/' +
          type +
          pagination +
          cropYear,
        obj
      )
      .pipe(map((response) => response));
  }

  getSustainability(
    domain: string,
    type: string,
    obj: SearchInput,
    applyCompanyIdFilter: boolean = true,
    paginationFrom: number = null
  ): Observable<any> {
    let pagination = '';
    if (paginationFrom != null && paginationFrom != 0) pagination = '?from=' + paginationFrom;

    let cropYear = '';
    if (pagination == '') {
      cropYear = '?cropYear=' + this.yearSelected;
    } else {
      cropYear = '&cropYear=' + this.yearSelected;
    }

    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(
        this.apiAnalyticsUrl +
          'analytics/supply-chain/' +
          domain +
          '/' +
          type +
          pagination +
          cropYear,
        obj
      )
      .pipe(map((response) => response));
  }

  getHarvestingTrend(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }
    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/harvesting-trend', obj)
      .pipe(map((response) => response));
  }

  getHarvestingYear(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/harvesting-per-year', obj)
      .pipe(map((response) => response));
  }

  getHarvestingFarmsCompared(
    obj: SearchInput,
    applyCompanyIdFilter: boolean = true
  ): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/harvesting-per-farm-compare', obj)
      .pipe(map((response) => response));
  }

  getCropHarvest(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/harvesting-per-crop', obj)
      .pipe(map((response) => response));
  }

  getCropHarvestPerFarm(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    const cropYear = '?cropYear=' + this.yearSelected;

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/harvesting-per-farm' + cropYear, obj)
      .pipe(map((response) => response));
  }

  getActivitiesTrend(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/activities-trend', obj)
      .pipe(map((response) => response));
  }

  getCropVarietyBreakdown(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/crop-variety-breakdown', obj)
      .pipe(map((response) => response));
  }

  getProductUsageStats(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/products-usage-stats', obj)
      .pipe(map((response) => response));
  }

  /**
   * Retrieves activity types per farm based on the given search input.
   *
   * @param {SearchInput} obj - The search input containing query filters.
   * @param {boolean} applyCompanyIdFilter - Whether to apply the company ID filter.
   * @returns {Observable<any>} An observable containing the response data.
   */
  getActivityTypesPerFarm(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    if (applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }
    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/activity-types-per-farm', obj)
      .pipe(map((response) => response));
  }

  /**
   * Retrieves other activity types per farm based on the given search input.
   *
   * @param {SearchInput} obj - The search input containing query filters.
   * @param {boolean} applyCompanyIdFilter- Whether to apply the company ID filter.
   * @returns {Observable<any>} An observable containing the response data.
   */
  getOtherActivityTypesPerFarm(
    obj: SearchInput,
    applyCompanyIdFilter: boolean = true
  ): Observable<any> {
    if (applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }
    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/activity-otherTypes-per-farm', obj)
      .pipe(map((response) => response));
  }

  getEmissionPerFarm(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/emission-per-farm', obj)
      .pipe(map((response) => response));
  }

  getEmissionMap(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/emission-per-field', obj)
      .pipe(map((response) => response));
  }

  getSustainabilityIndicators(
    obj: SearchInput,
    indicatorCode,
    applyCompanyIdFilter: boolean = true
  ): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    let path = this.apiAnalyticsUrl + 'analytics/custom/sustainability-indicators';
    if (indicatorCode) {
      path = path + '/indicatorCode';
    }
    return this.http.post<any>(path, obj).pipe(map((response) => response));
  }

  getNUE(obj: SearchInput, applyCompanyIdFilter: boolean = true): Observable<any> {
    // If chain filters are applied those are added to the request
    if (obj && obj.queryFilters && applyCompanyIdFilter && this.companyIdToFilter.length > 0) {
      obj.queryFilters.push({ field: 'companyId', values: this.companyIdToFilter });
    }

    return this.http
      .post<any>(this.apiAnalyticsUrl + 'analytics/custom/NitrogenUseEfficiency', obj)
      .pipe(map((response) => response));
  }
}

// ESEMPI DI CHIAMATE
// let sub = [{ "name": "", "aggregationType": "SUM", "field": "size" }, { "name": "", "aggregationType": "AVG", "field": "size" }];
//     let s = this.analyticsService.generateSearchObject([{ "field": "year", "values": ["2018"] }], AggregationType.TERM, "commodity.name",sub);
//     this.analyticsService.getAnalytics("crops", "groupBy", s).subscribe(res => {
//       console.log(res)
//     });

//     let sub1 = [{ "name": "", "aggregationType": "SUM", "field": "workedHectares" }];
//     let searchSeeding = this.analyticsService.generateSearchObject([{ "field": "crop.year", "values": ["2018"] },{ "field": "done", "values": ["true"] }], AggregationType.TERM, "type",sub1);
//       this.analyticsService.getAnalytics("fieldactivities", "groupBy", searchSeeding).subscribe(res => {
//         // let percentageValue = (100 * res.value) / this.totalSizeCompanies;
//         // this.seedingPercentage = Math.round(percentageValue);
//         console.log("<--lista-->")
//         console.log(res)
//       });

//       // let subx = [{ "name": "", "aggregationType": "SUM", "field": "workedHectares" }];
//     let x = this.analyticsService.generateSearchObject([{ "field": "crop.year", "values": ["2018"] },{ "field": "type", "values": ["harvesting"] },/*{ "field": "crop.commodity.name", "values": ["nome"] }*/], AggregationType.LIST, "");
//       this.analyticsService.getAnalytics("fieldactivities", "list", x).subscribe(res => {
//         console.log("<--andamento-->")
//         console.log(res)
//       });
